import { NgClass, NgOptimizedImage, NgStyle } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    QueryList,
    Renderer2,
    ViewChildren,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ImagesConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { TrustPipe } from '@frontend/sports/common/core/utils/dom';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { WindowEventService } from '@frontend/sports/common/core/utils/window-event';
import { ArrowDirection, NavigationMode, SlideComponent, SlideshowComponent, SlideshowMode } from '@frontend/sports/slideshow/feature';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsButton } from '@frontend/ui/button';
import { DeviceService, NativeAppService, WINDOW } from '@frontend/vanilla/core';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';

import { BannerJackpotComponent } from '../banner/banner-jackpot.component';
import { ImageQualityService } from '../common/image-quality.service';
import { Promotion } from '../crm-offer-data/crm-offer.model';
import { CrmPromotionModule } from '../crm-promotion/crm-promotion.module';
import { EventBridgeService } from '../event-bridge/event-bridge.service';
import { UrlHelperService } from '../navigation-core/url-helper.service';
import { Alignment, EnhancedTeaserViewModel } from './teaser.models';

@Component({
    selector: 'ms-teaser',
    templateUrl: 'teaser.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BannerJackpotComponent,
        CrmPromotionModule,
        NgOptimizedImage,
        NgClass,
        NgStyle,
        TrustPipe,
        IconCustomComponent,
        SlideshowComponent,
        SlideComponent,
        DsButton,
    ],
})
export class TeaserComponent implements OnChanges, AfterViewInit {
    @Input() teasers: EnhancedTeaserViewModel[] = [];
    @Input() promotions: Promotion[] = [];
    @HostBinding('class') className = 'teaser teaser-list';

    currentSlideIndex = 0;

    slideMode = SlideshowMode.OneSlide;
    delay = 8000;

    @ViewChildren('slide') allSlides: QueryList<ElementRef>;

    private imageElements: HTMLImageElement[] = [];
    private teaserMaxHeight = 240;
    private teaserImageRatio = 3.2;
    private cropRange = {
        min: 767,
        max: 932,
    };

    private arrows = {
        width: 50,
        height: 50,
        marginOnThreeTeasers: 16,
        marginOnOneTeasers: 4,
    };

    arrowLeftRightValue: number;
    maxSlideWidth = false;
    navigationMode: NavigationMode;
    containerWidth: number;
    overlayArea: {
        position: string;
        width: string;
    };

    private readonly destroyRef = inject(DestroyRef);
    private readonly _window = inject(WINDOW);

    constructor(
        private urlHelper: UrlHelperService,
        private eventBridge: EventBridgeService,
        private nativeApp: NativeAppService,
        private tracking: TrackingService,
        private elementRef: ElementRef<HTMLElement>,
        private device: DeviceService,
        private renderer: Renderer2,
        private windowEvent: WindowEventService,
        private changeDetector: ChangeDetectorRef,
        private timer: TimerService,
        public sitecore: Sitecore,
        private imagesConfig: ImagesConfig,
        private imageQualityService: ImageQualityService,
    ) {
        if (!this.device.isMobile && !this.device.isTablet) {
            this.calcuteWidth(this._window.innerWidth);
        }

        this.device.orientation.pipe(takeUntilDestroyed()).subscribe(this.toggleSlideMode);
    }

    private calcuteWidth(resolution: any) {
        this.cropRange.max = resolution * 0.48;

        this.cropRange.min = resolution * 0.4;
    }

    ngOnChanges(changes: ISimpleChanges<TeaserComponent>): void {
        this.toggleSlideMode();

        if (changes.teasers?.currentValue?.length) {
            this.teasers.forEach((t) => this.setTeaserLink(t));
            this.currentSlideIndex = 0;
            [0, 1, this.teasers.length - 1].forEach((index) => {
                if (this.teasers[index]) {
                    this.teasers[index].visibleTeaser = true;
                }
            });
        }
    }

    ngAfterViewInit(): void {
        this.allSlides.forEach((slide: ElementRef) => this.imageElements.push(slide.nativeElement));
        this.setContainerWidth();

        this.windowEvent.resize
            .asObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.setContainerWidth());
    }

    infoClick(event: Event, teaser: EnhancedTeaserViewModel): void {
        event.preventDefault();
        teaser.extract = !teaser.extract;
    }

    teaserClick(event: Event, teaser: EnhancedTeaserViewModel, index: number): void {
        this.trackClick(teaser, index);
    }

    totalCount(): number {
        return this.teasers[0]?.originalTeasersCount || 0 + this.promotions.length;
    }

    private trackClick(teaser: EnhancedTeaserViewModel, index: number): void {
        const bannerPosition = index + this.promotions.length;

        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Sports Teaser Banners',
            [trackingConstants.COMPONENT_LABEL_EVENT]: teaser.title ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'Click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: teaser.buttonText ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'CTA',
            [trackingConstants.COMPONENT_URL_CLICKED]: teaser.href ?? NOT_APPLICABLE,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: bannerPosition.toString(),
        };

        if (this.nativeApp.isNative && teaser.sliderGameId) {
            this.eventBridge.notifySliderGameOpening(teaser.sliderGameId);
        } else if (!this.nativeApp.isNative && !teaser.sliderGameId && !teaser.event && teaser.href) {
            this.tracking.track(trackingConstants.EVENT_CLICKS, trackingData);
        }
    }

    private setTeaserLink(teaser: EnhancedTeaserViewModel): void {
        if (teaser.event) {
            teaser.href = this.urlHelper.getEventUrl(teaser.event);
        }

        if (!teaser.href) {
            teaser.href = '#';
        }

        if (teaser.href.startsWith('/')) {
            teaser.href = this.urlHelper.getUrl(teaser.href);
        }

        const nav = <any>this._window.navigator;

        if (!!nav.standalone || this.nativeApp.isNative) {
            teaser.target = '_self';
        }

        if (this.imagesConfig.imageQualityParams?.homePage?.rotatingTeaser)
            teaser.imageUrl = this.imageQualityService.setImageQuality(teaser.imageUrl, this.imagesConfig.imageQualityParams.homePage.rotatingTeaser);
    }

    private toggleSlideMode = () => {
        if (!this.device.isMobile) {
            this.navigationMode = NavigationMode.Arrows;
        } else {
            this.navigationMode = NavigationMode.Swipe;
        }

        if (this.containerWidth) {
            const teaserCount = this.teasers?.length + this.promotions.length;
            this.slideMode = this.containerWidth > this.cropRange.max && teaserCount > 1 ? SlideshowMode.ThreeSlides : SlideshowMode.OneSlide;
        }
    };

    setNextVisibleTeaserIndex(index: number): number {
        const wrapAroundToLastSlide = this.currentSlideIndex === 0 && index === this.teasers.length - 1;
        const wrapAroundToFirstSlide = this.currentSlideIndex === this.teasers.length - 1 && index === 0;

        if ((this.currentSlideIndex < index && !wrapAroundToLastSlide) || wrapAroundToFirstSlide) {
            return index + 1;
        }

        return index - 1;
    }

    slideChanged(index: number): void {
        const nextIndex = this.setNextVisibleTeaserIndex(index);

        if (this.teasers[nextIndex]) {
            this.teasers[nextIndex].visibleTeaser = true;
        }

        this.currentSlideIndex = index;
        this.changeDetector.detectChanges();
    }

    isCurrentSlide(slideIndex: number): boolean {
        return slideIndex === this.currentSlideIndex;
    }

    private setContainerWidth(): void {
        this.timer.setAnimationFrame(() => {
            const width = this.elementRef.nativeElement.clientWidth;

            if (width) {
                this.containerWidth = width;

                let teaserHeight = this.containerWidth / this.teaserImageRatio;

                if (teaserHeight >= this.teaserMaxHeight) {
                    teaserHeight = this.teaserMaxHeight;
                }

                if (this.containerWidth > this.cropRange.min && this.containerWidth < this.cropRange.max) {
                    this.imageElements.forEach((e) => this.setStyle(e, teaserHeight, true));
                } else {
                    this.imageElements.forEach((e) => this.setStyle(e, teaserHeight));

                    this.arrowLeftRightValue =
                        (this.containerWidth - this.cropRange.min) / 2 - (this.arrows.marginOnThreeTeasers + this.arrows.width);
                }

                if (this.containerWidth > this.cropRange.max) {
                    this.arrowLeftRightValue = (this.containerWidth - this.cropRange.min) / 2 - this.arrows.marginOnThreeTeasers - this.arrows.width;
                    this.maxSlideWidth = true;
                } else {
                    this.arrowLeftRightValue = this.arrows.marginOnOneTeasers;
                    this.maxSlideWidth = false;
                }

                if (this.containerWidth > this.cropRange.min) {
                    this.overlayArea = {
                        position: `${(this.containerWidth - this.cropRange.min) / 2 + this.cropRange.min}px`,
                        width: `${(this.containerWidth - this.cropRange.min) / 2}px`,
                    };
                }
            }

            this.toggleSlideMode();
            this.changeDetector.detectChanges();
        });
    }

    private setStyle(el: HTMLElement, height: number, isCroppable?: boolean): void {
        if (isCroppable) {
            this.renderer.setStyle(el, 'width', `100%`);
            this.renderer.setStyle(el, 'height', `${height}px`);
        } else {
            this.renderer.setStyle(el, 'width', `${height * this.teaserImageRatio}px`);
            this.renderer.setStyle(el, 'height', `${height}px`);
        }
    }

    private getSelectedTeaser(): EnhancedTeaserViewModel | undefined {
        const teaserIndex = this.currentSlideIndex - this.promotions.length;

        return teaserIndex >= 0 ? this.teasers[teaserIndex] : undefined;
    }

    track(event?: { navigationMode: NavigationMode; direction: string }): void {
        const selectedTeaser = this.getSelectedTeaser();
        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Sports Teaser Banners',
            ...(selectedTeaser && { [trackingConstants.COMPONENT_LABEL_EVENT]: selectedTeaser.title }),
            [trackingConstants.COMPONENT_ACTION_EVENT]: event ? 'Arrow Click' : 'CTA Click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: `Teaser_${this.currentSlideIndex + 1}`,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: event
                ? `Arrow ${event.direction === ArrowDirection.Right ? 'Back' : 'Forward'} `
                : this.sitecore.globalMessages.BetNowBtnOnTeaser,
        };

        this.tracking.track(trackingConstants.EVENT_CLICKS, trackingData);
    }

    getTeaserAlignment(teaser: EnhancedTeaserViewModel): string {
        switch (teaser.textAlignment) {
            case Alignment.Left:
                return 'alignment-left';
            case Alignment.Right:
                return 'alignment-right';
            default:
                return 'alignment-center';
        }
    }
}
